import { onDispose } from "@/utils/component";
import { useMousePosition } from "@/utils/hooks/useMousePosition";
import Icon from "components/icon";
import type { FunctionalComponent } from "vue";
import type { JSX } from "vue/jsx-runtime";
import type { UseDrawer } from "~/composables/useDrawer";
import { globalDrawers, popDrawer, type IDrawer } from "~/composables/useDrawers";
import { hideFeatureBase, showFeatureBase } from "@/integrations/featurebase/utils";
import { globalModals } from "components/modal/hooks/useModals";


const TopBar = defineComponent<{
  actions?: FunctionalComponent<{ hook: UseDrawer }> | MaybeRef<JSX.Element>;
  drawer: IDrawer;
}>((props, ctx) => {
  const actionsContent = computed(() => {
    const content = unref(props.actions);
    if (typeof content === "function") return <content hook={props.drawer.hook} />;
    return content;
  });
  const handleClose = () => {
    popDrawer();
  }
  return () => {
    return (
      <div class="w-full h-[47px] border-b border-gray-300 grid items-center px-4">
        <div class="w-full h-full grid grid-cols-[1fr_max-content] justify-between items-center">
          {ctx.slots.default ? ctx.slots.default() : ""}

          <div class="inline-flex items-center gap-2">
            {
              actionsContent.value && actionsContent.value
            }
            <span onClick={handleClose} class="cursor-pointer text-gray-400 hover:text-gray-500 transition-colors min-w-4 min-h-4 h-full grid items-center justify-center">
              <Icon stroke={"currentColor"} size="0.75rem" src={async () => import('assets/img/svg/icons/close2.svg?raw')} />
            </span>
          </div>
        </div>
      </div>
    );
  };
}, { props: ['actions', 'drawer'] });

const Dragger = defineComponent<{
  direction: MaybeRef<number>;
  onMousedown?: any;
}>((props) => {
  const direction = computed(() => unref(props.direction));
  return () => {
    return (
      <div
        class={[
          "absolute top-0 bottom-0 my-auto w-[16px] h-full grid items-center justify-end px-5 cursor-w-resize hoverDragger",
          direction.value > 0 ? "left-[-18px]" : "right-[-24px]"
        ]}
      >
        <div class="w-1.5 h-6 bg-primary-500 rounded-xl"></div>
      </div>
    );
  };
}, { props: ['direction'] });

export const Drawer = defineComponent<{
  drawer: IDrawer;
}>(
  (props) => {
    const draggerRef = ref();
    const wrapperRef = ref();

    const dragging = ref<boolean>(false);

    const mouse = useMousePosition();

    const handleMouseDown = (ev: MouseEvent) => {
      ev.preventDefault();
      dragging.value = true;
    };

    const handleMouseUp = (ev: MouseEvent) => {
      ev.preventDefault();
      dragging.value = false;
    };
    window.addEventListener("mouseup", handleMouseUp);

    onDispose(() => {
      window.removeEventListener("mouseup", handleMouseUp);
    });

    watch(mouse.position, (pos) => {
      if (!dragging.value) return;
      const w = 1.0 - pos.x / window.innerWidth;

      props.drawer.hook.setWidth(() => {
        return (props.drawer.hook.direction.value > 0 ? (w) : (1.0 - w)) * 100.0;
      });
    });

    const topBarContent = computed(() => {
      const content = unref(props.drawer.topBar);
      if (typeof content === "function") return <content />;
      return content;
    });

    const bodyContent = computed(() => {
      const content = unref(props.drawer.body);
      if (typeof content === "function") return <content />;
      return content;
    });

    hideFeatureBase();

    onBeforeUnmount(() => {
      const remainingDrawers = globalDrawers.count.value > 1;
      const hasFullscreenModals = Array.from(globalModals.modals.value.values())
        .some(modalRef => unref(modalRef).fullscreen === true);

      if (!remainingDrawers && !hasFullscreenModals) {
        showFeatureBase();
      }
    });

    return () => {
      return (
        <div
          ref={wrapperRef}
          style={{
            ...props.drawer.hook.style.value,
            height: '100vh',
            position: 'relative'
          }}
          class={[
            "bg-gray-50 h-full flex flex-col shadow-[0px_1px_6px_0px_rgba(0,0,0,0.25)] border-gray-200 relative hover:border-l-2  border [&:has(.hoverDragger:hover)]:border-primary-500",
            props.drawer.hook.direction.value > 0 ? "place-self-end border-l" : "place-self-start border-r hover:border-r-2 "
          ]}
        >
          <TopBar drawer={props.drawer} actions={props.drawer.topBarActions} class="flex-none">{topBarContent.value}</TopBar>
          <Dragger direction={props.drawer.hook.direction} onMousedown={handleMouseDown} ref={draggerRef} />
          <div class="flex-1 w-full">{bodyContent.value}</div>
        </div>
      );
    };
  },
  { props: ["drawer"] },
);
